import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import {
  useCallback,
  useEffect,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  getAccountingDashboard,
} from "../../services/accountingService"



const formatMoney = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))


const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("fr-FR").format(date)
}


const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || fallback
  }

  return error?.message || fallback
}



function KpiCard({ title, value, subtitle }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>

      <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
        {value}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Paper>
  )
}


export default function AccountingDashboardPage() {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)

  const toIsoDate = (value) =>
    value.toISOString().slice(0, 10)

  const [dateFrom, setDateFrom] = useState(toIsoDate(firstDay))
  const [dateTo, setDateTo] = useState(toIsoDate(today))
  const [data, setData] = useState({ recent_entries: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const result = await getAccountingDashboard({
        date_from: dateFrom,
        date_to: dateTo,
      })

      setData(result || { recent_entries: [] })
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Impossible de charger le dashboard Finance."
        )
      )
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo])

  useEffect(() => {
    loadData()
  }, [loadData])

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Dashboard Finance
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Produits, charges, trésorerie, créances et dettes comptables.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />

            <TextField
              size="small"
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />

            <Button
              variant="outlined"
              onClick={loadData}
              disabled={loading}
            >
              Actualiser
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Produits"
            value={formatMoney(data.revenue)}
            subtitle="Produits comptabilisés"
          />

          <KpiCard
            title="Charges"
            value={formatMoney(data.expenses)}
            subtitle="Charges comptabilisées"
          />

          <KpiCard
            title="Résultat"
            value={formatMoney(data.net_result)}
            subtitle="Produits - charges"
          />

          <KpiCard
            title="Trésorerie"
            value={formatMoney(data.cash_balance)}
            subtitle="Caisse + banque + mobile money"
          />

          <KpiCard
            title="Créances clients"
            value={formatMoney(data.customer_receivables)}
            subtitle="Solde du compte Clients"
          />

          <KpiCard
            title="Dettes fournisseurs"
            value={formatMoney(data.supplier_payables)}
            subtitle="Solde du compte Fournisseurs"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={800}>
              Écritures récentes
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {data.entries_count || 0} écriture(s) sur la période.
            </Typography>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 760,
                borderCollapse: "collapse",
                "& th, & td": {
                  p: 1.5,
                  textAlign: "left",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                "& th": {
                  bgcolor: "action.hover",
                  fontWeight: 800,
                },
              }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>N° écriture</th>
                  <th>Journal</th>
                  <th>Description</th>
                  <th>Source</th>
                  <th>Montant</th>
                </tr>
              </thead>

              <tbody>
                {data.recent_entries?.length ? (
                  data.recent_entries.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.entry_date)}</td>
                      <td>{row.entry_code}</td>
                      <td>{row.journal_code}</td>
                      <td>{row.description}</td>
                      <td>{row.source_type || "MANUAL"}</td>
                      <td>{formatMoney(row.total_debit, row.currency || "USD")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "32px" }}>
                      Aucune écriture comptable.
                    </td>
                  </tr>
                )}
              </tbody>
            </Box>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  )
}
